import { Link } from 'react-router-dom';
import {
  linkedEvidence,
  relativeAge,
  type Evidence,
  type FieldAssignment,
  type User,
} from '@kilroy/shared';
import { IconCompass, IconPolygon } from './icons';
import './assignment-card.css';

/* An open assignment.
 *
 * Leads with who it came from. On a seven-person team that is the single most
 * useful fact — "Allan wants this walked" carries more than a title does, and
 * it is what makes the loop feel like people rather than tickets.
 *
 * The icon is not decoration: a dashed polygon here is the same mark the map
 * draws the assignment with, so the card and the shape on the ground read as
 * one object.
 */

export function AssignmentCard({
  assignment,
  evidence,
  author,
}: {
  assignment: FieldAssignment;
  evidence: Evidence[];
  author: User;
}) {
  const found = linkedEvidence(assignment, evidence).length;
  const tags = [
    ...(assignment.expected_alteration_type ?? []),
    ...(assignment.expected_rock_type ? [assignment.expected_rock_type] : []),
    ...(assignment.expected_feature_type ? [assignment.expected_feature_type] : []),
  ];
  const target = Math.max(tags.length || 1, found);
  const confirming = assignment.assignment_type === 'confirm_existing';

  return (
    <Link to={`/assignment/${assignment.id}`} className="card asg">
      <div className="asg__h">
        <span className={`ic ${confirming ? 'ic--warn' : 'ic--brand'}`}>
          {confirming ? <IconCompass size={17} /> : <IconPolygon size={17} />}
        </span>
        <span className="grow">
          <span className="asg__who">
            <b>{author.name}</b> · {relativeAge(assignment.created_at)}
          </span>
          <div className="asg__t">{assignment.title}</div>
        </span>
        {assignment.priority === 'high' && <span className="badge">High</span>}
      </div>

      {assignment.instructions_text && (
        <p className="asg__d">{assignment.instructions_text}</p>
      )}

      {tags.length > 0 && (
        <div className="asg__tags">
          {tags.slice(0, 4).map((t) => (
            <span key={t} className="tag">
              {t.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}

      <div className="asg__f">
        <span className="bar">
          <i style={{ width: `${Math.min(100, (found / target) * 100)}%` }} />
        </span>
        <span className="asg__c mono">
          {found} of {target}
        </span>
      </div>
    </Link>
  );
}
